/**
 * Custom Hook: useDashboard
 * Fetches dashboard data with loading and error states
 */

import { useCallback, useState, useEffect, useRef } from 'react';
import dashboardAPI from '../services/dashboardAPI';

const isValidRecord = (item) => {
  if (!item) return false;
  if (!item.date) return false;
  const amount = Number(item.amount);
  if (!amount || amount === 0) return false;
  if (item.voucherNumber && String(item.voucherNumber).startsWith('nodate-')) return false;
  return true;
};

const applyDateRange = (items, from, to) => {
  if (!from && !to) return items;
  return items.filter((item) => {
    if (!item?.date) return false;
    const date = new Date(item.date);
    if (from) {
      const minDate = new Date(from);
      if (date < minDate) return false;
    }
    if (to) {
      const maxDate = new Date(to);
      if (date > maxDate) return false;
    }
    return true;
  });
};

const cleanRecords = (records, from, to) => {
  if (!Array.isArray(records)) return [];
  return applyDateRange(records.filter(isValidRecord), from, to);
};

export const useDashboard = ({ companyId, from, to } = {}) => {
  const [summary, setSummary] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dashboardAPI.getSummary({ companyId, from, to });
        setSummary(response?.data ?? null);
        setLastUpdated(response?.timestamp ? new Date(response.timestamp) : new Date());
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to fetch dashboard data');
        setSummary(null);
        setLastUpdated(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();

    // Refresh every 5 minutes when filters remain unchanged
    const interval = setInterval(fetchDashboard, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [companyId, from, to]);

  return { summary, lastUpdated, loading, error };
};

/**
 * Custom Hook: useSalesVouchers
 */
export const useSalesVouchers = (companyId, from, to) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dashboardAPI.getSalesVouchers(companyId, from, to);
        const cleaned = cleanRecords(response?.data ?? [], from, to);
        setData(cleaned);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to fetch sales vouchers');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [companyId, from, to]);

  return { data, loading, error };
};

/**
 * Custom Hook: usePurchaseVouchers
 */
export const usePurchaseVouchers = (companyId, from, to) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dashboardAPI.getPurchaseVouchers(companyId, from, to);
        const cleaned = cleanRecords(response?.data ?? [], from, to);
        setData(cleaned);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to fetch purchase vouchers');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [companyId, from, to]);

  return { data, loading, error };
};

/**
 * Custom Hook: useReceivables
 */
export const useReceivables = (companyId) => {
  const [data, setData] = useState({ data: [], total: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dashboardAPI.getReceivables(companyId);
        setData(response ?? { data: [], total: 0, count: 0 });
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to fetch receivables');
        setData({ data: [], total: 0, count: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [companyId]);

  return { data, loading, error };
};

/**
 * Custom Hook: usePayables
 */
export const usePayables = (companyId) => {
  const [data, setData] = useState({ data: [], total: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dashboardAPI.getPayables(companyId);
        setData(response ?? { data: [], total: 0, count: 0 });
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to fetch payables');
        setData({ data: [], total: 0, count: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [companyId]);

  return { data, loading, error };
};

/**
 * Custom Hook: useRecentTransactions
 */
export const useRecentTransactions = (limit = 10, companyId, from, to) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dashboardAPI.getRecentTransactions(limit, companyId, from, to);
        const cleaned = cleanRecords(response?.data ?? [], from, to);
        setData(cleaned);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to fetch transactions');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [limit, companyId, from, to]);

  return { data, loading, error };
};

/**
 * ADDED — Custom Hook: useSyncTrigger
 * Starts a background Tally sync (POST /api/sync/run-async) and polls
 * GET /api/sync/status until it finishes, instead of holding one request
 * open for minutes. Purely additive — no existing hook here is modified,
 * and nothing currently calls the old blocking POST /api/sync/run, which
 * still works unchanged for any other caller.
 */
export const useSyncTrigger = () => {
  const [status, setStatus] = useState('idle'); // idle | running | completed | failed
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (status !== 'running') return undefined;

    const poll = async () => {
      try {
        const response = await dashboardAPI.getSyncStatus();
        const currentJob = response?.job;
        setJob(currentJob ?? null);
        if (currentJob?.status === 'completed' || currentJob?.status === 'failed') {
          setStatus(currentJob.status);
        }
      } catch (err) {
        // Transient poll failure — keep polling, don't flip to failed on a
        // single network hiccup.
        console.error('Sync status poll error:', err.message);
      }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [status]);

  const startSync = async () => {
    try {
      setError(null);
      setStatus('running');
      const response = await dashboardAPI.triggerSync();
      setJob(response?.job ?? null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to start sync');
      setStatus('failed');
    }
  };

  return { status, job, error, startSync };
};

/**
 * ADDED — normalizes a raw voucher row (receipt/payment/sales/purchase) coming
 * from the /db endpoints into the shape the dashboard tables/KPIs expect.
 * Purely additive; existing hooks are untouched.
 */
const normalizeVoucher = (row, type, companyName) => ({
  id: `${type}-${row.companyId || ''}-${row.id ?? row.voucherNumber}`,
  type,
  // guid is the drill-down key — /db/companies/:id/voucher/:guid expands a row
  // into its full ledger lines. Carried through so any table row can open it.
  guid: row.guid,
  voucherId: row.voucherNumber,
  voucherNumber: row.voucherNumber,
  // The /db endpoints return the Voucher model, whose column is
  // partyLedgerName; `row.party` never existed on it, so this column rendered
  // as '—' for every row until now.
  party: row.partyLedgerName || row.party || '—',
  amount: Math.abs(Number(row.amount) || 0),
  date: row.date,
  narration: row.narration || '',
  reference: row.reference || '',
  companyId: row.companyId,
  companyName: companyName || '',
});

/**
 * ADDED — fetches every voucher type (receipts, payments, sales, purchases)
 * for either a single company or ALL companies, then merges them into one
 * normalized array. Uses only pre-existing endpoints:
 *   /db/companies/:id/receipts  /payments  /sales  /purchases
 * When companyId === 'all' it fans out over the supplied companies list and
 * concatenates the results. High limit so totals/counts are accurate.
 */
export const useAllVouchers = (companyId, companies = [], from, to) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const companyKey = (companies || []).map((c) => c.id).join(',');

  useEffect(() => {
    let cancelled = false;

    const fetchForCompany = async (cid, cname) => {
      const LIMIT = 10000;
      const [receipts, payments, sales, purchases] = await Promise.all([
        dashboardAPI.getReceipts(cid, LIMIT, 0).catch(() => ({ data: [] })),
        dashboardAPI.getPayments(cid, LIMIT, 0).catch(() => ({ data: [] })),
        dashboardAPI.getSalesByCompany(cid, LIMIT, 0).catch(() => ({ data: [] })),
        dashboardAPI.getPurchasesByCompany(cid, LIMIT, 0).catch(() => ({ data: [] })),
      ]);
      return [
        ...(receipts?.data ?? []).map((r) => normalizeVoucher(r, 'Receipt', cname)),
        ...(payments?.data ?? []).map((r) => normalizeVoucher(r, 'Payment', cname)),
        ...(sales?.data ?? []).map((r) => normalizeVoucher(r, 'Sales', cname)),
        ...(purchases?.data ?? []).map((r) => normalizeVoucher(r, 'Purchase', cname)),
      ];
    };

    const run = async () => {
      try {
        setLoading(true);
        setError(null);

        let targets = [];
        if (companyId && companyId !== 'all') {
          const match = (companies || []).find((c) => String(c.id) === String(companyId));
          targets = [{ id: companyId, name: match?.name || '' }];
        } else {
          targets = (companies || []).map((c) => ({ id: c.id, name: c.name }));
        }

        if (targets.length === 0) {
          if (!cancelled) setData([]);
          return;
        }

        const batches = await Promise.all(targets.map((t) => fetchForCompany(t.id, t.name)));
        let merged = batches.flat();
        merged = applyDateRange(
          merged.filter((v) => v.date && v.amount !== 0),
          from,
          to,
        );
        merged.sort((a, b) => new Date(b.date) - new Date(a.date));

        if (!cancelled) setData(merged);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to fetch vouchers');
          setData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // companyKey captures the companies list identity without re-running on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, companyKey, from, to]);

  return { data, loading, error };
};

/**
 * ADDED — fetches journal vouchers for either a single company or ALL
 * companies (fans out and merges, same pattern as useAllVouchers), so the
 * Journal Entries section can filter by its own company + period.
 */
export const useJournalVouchers = (companyId, companies = [], from, to) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const companyKey = (companies || []).map((c) => c.id).join(',');

  useEffect(() => {
    let cancelled = false;

    const fetchForCompany = async (cid, cname) => {
      const response = await dashboardAPI.getJournalVouchersByCompany(cid, 10000, 0).catch(() => ({ data: [] }));
      return (response?.data ?? []).map((r) => normalizeVoucher(r, 'Journal', cname));
    };

    const run = async () => {
      try {
        setLoading(true);
        setError(null);

        let targets = [];
        if (companyId && companyId !== 'all') {
          const match = (companies || []).find((c) => String(c.id) === String(companyId));
          targets = [{ id: companyId, name: match?.name || '' }];
        } else {
          targets = (companies || []).map((c) => ({ id: c.id, name: c.name }));
        }

        if (targets.length === 0) {
          if (!cancelled) setData([]);
          return;
        }

        const batches = await Promise.all(targets.map((t) => fetchForCompany(t.id, t.name)));
        let merged = batches.flat();
        merged = applyDateRange(merged.filter((v) => v.date), from, to);
        merged.sort((a, b) => new Date(b.date) - new Date(a.date));

        if (!cancelled) setData(merged);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to fetch journal entries');
          setData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, companyKey, from, to]);

  return { data, loading, error };
};

/**
 * ADDED — fetches the ledger master list (with balances) for a single
 * company or ALL companies. Ledger balances are a point-in-time snapshot
 * (no per-transaction date on the model), so there is no date-range param
 * here — only company scoping.
 */
export const useLedgersByCompany = (companyId, companies = []) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const companyKey = (companies || []).map((c) => c.id).join(',');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);

        let targets = [];
        if (companyId && companyId !== 'all') {
          const match = (companies || []).find((c) => String(c.id) === String(companyId));
          targets = [{ id: companyId, name: match?.name || '' }];
        } else {
          targets = (companies || []).map((c) => ({ id: c.id, name: c.name }));
        }

        if (targets.length === 0) {
          if (!cancelled) setData([]);
          return;
        }

        const batches = await Promise.all(targets.map(async (t) => {
          const response = await dashboardAPI.getLedgersByCompany(t.id).catch(() => ({ data: [] }));
          return (response?.data ?? []).map((l) => ({ ...l, companyName: t.name }));
        }));

        if (!cancelled) setData(batches.flat());
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to fetch ledgers');
          setData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, companyKey]);

  return { data, loading, error };
};

/**
 * Fetches the trial balance for a single company or ALL companies, optionally
 * for a specific window.
 *
 * ON THE DATE RANGE (this used to say a range did not apply):
 *   With no from/to the backend serves the stored snapshot, exactly as before.
 *   With a window it computes that window live — and correctly for ANY window,
 *   not just the two that get stored: the SQL folds every posting before `from`
 *   into the opening balance, so `closing = opening + movements` holds whatever
 *   dates are asked for. See utils/financials.js BASE_SQL.
 *
 *   The window can only show periods that have actually been synced, though —
 *   a range earlier than the data in the database returns zero movement, not
 *   an error.
 */
export const useTrialBalanceByCompany = (companyId, companies = [], from = '', to = '') => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const companyKey = (companies || []).map((c) => c.id).join(',');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);

        let targets = [];
        if (companyId && companyId !== 'all') {
          const match = (companies || []).find((c) => String(c.id) === String(companyId));
          targets = [{ id: companyId, name: match?.name || '' }];
        } else {
          targets = (companies || []).map((c) => ({ id: c.id, name: c.name }));
        }

        if (targets.length === 0) {
          if (!cancelled) setData([]);
          return;
        }

        const batches = await Promise.all(targets.map(async (t) => {
          const response = await dashboardAPI
            .getTrialBalanceByCompany(t.id, from, to)
            .catch(() => ({ data: [] }));
          return (response?.data ?? []).map((r) => ({ ...r, companyName: t.name }));
        }));

        if (!cancelled) setData(batches.flat());
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to fetch trial balance');
          setData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, companyKey, from, to]);

  return { data, loading, error };
};

/**
 * ADDED — drill-down level 1: the full Tally Ledger view for one ledger.
 * Fetches nothing until a ledger is actually selected, so opening the
 * dashboard costs no extra requests.
 */
export const useLedgerStatement = (companyId, { ledgerGuid, ledgerName, from, to } = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [error, setError] = useState(null);
  const [pageOffset, setPageOffset] = useState(0);

  const prevFilters = useRef({ companyId, ledgerGuid, ledgerName, from, to });
  const filtersChanged = prevFilters.current.companyId !== companyId ||
                         prevFilters.current.ledgerGuid !== ledgerGuid ||
                         prevFilters.current.ledgerName !== ledgerName ||
                         prevFilters.current.from !== from ||
                         prevFilters.current.to !== to;
                         
  if (filtersChanged) {
    setPageOffset(0);
    prevFilters.current = { companyId, ledgerGuid, ledgerName, from, to };
  }

  useEffect(() => {
    if (!companyId || (!ledgerGuid && !ledgerName)) {
      setData(null);
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dashboardAPI.getLedgerStatement(companyId, {
          ledgerGuid, ledgerName, from, to,limit: 500, offset: pageOffset,
        });
        if (!cancelled) setData(response?.data ?? null);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load ledger');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [companyId, ledgerGuid, ledgerName, from, to, pageOffset]);

  /**
   * Pulls the remaining pages so the statement holds every entry in the
   * period. Printing needs this: the screen shows one page, but a printed
   * ledger that silently stops at row 500 would be wrong rather than merely
   * short. The server caps a request at 2,000 rows, so heavy ledgers arrive in
   * several passes; running balances stay correct across them because the
   * server computes them over the whole set before paging.
   *
   * Returns the complete row list to the caller, since setState is async and
   * a print triggered straight after would otherwise still see the old rows.
   */
  const loadAll = useCallback(async () => {
    if (!data || data.rows.length >= data.count) return data?.rows ?? [];

    setLoadingAll(true);
    try {
      const CHUNK = 2000;
      const rows = [...data.rows];
      while (rows.length < data.count) {
        // eslint-disable-next-line no-await-in-loop
        const response = await dashboardAPI.getLedgerStatement(companyId, {
          ledgerGuid, ledgerName, from, to, limit: CHUNK, offset: rows.length,
        });
        const batch = response?.data?.rows ?? [];
        if (batch.length === 0) break; // guard against a never-ending loop
        rows.push(...batch);
      }
      setData((d) => (d ? { ...d, rows } : d));
      return rows;
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load all entries');
      return data.rows;
    } finally {
      setLoadingAll(false);
    }
  }, [data, companyId, ledgerGuid, ledgerName, from, to]);

const nextPage = useCallback(() => {
    if (data && data.offset + data.limit < data.count) {
      setPageOffset(data.offset + data.limit);
    }
  }, [data]);

  const prevPage = useCallback(() => {
    if (data && data.offset > 0) {
      setPageOffset(Math.max(0, data.offset - data.limit));
    }
  }, [data]);

  return { data, loading, loadingAll, error, loadAll, nextPage, prevPage };
};

/**
 * ADDED — drill-down level 2: one complete voucher, exactly as Tally shows it.
 */
export const useVoucherDetail = (companyId, guid) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId || !guid) {
      setData(null);
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dashboardAPI.getVoucherDetail(companyId, guid);
        if (!cancelled) setData(response?.data ?? null);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load voucher');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [companyId, guid]);

  return { data, loading, error };
};

/**
 * Custom Hook: useCompanies
 */
export const useCompanies = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dashboardAPI.getCompanies();
        setData(response?.data ?? []);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to fetch company list');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, []);

  return { data, loading, error };
};


export const useInventorySummary = (companyId) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setData([]);
      return;
    }

    let cancelled = false;

    const fetchInventory = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await dashboardAPI.getInventory(companyId);

        if (!cancelled) {
          setData(response?.data || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
            err.message ||
            "Failed to load inventory summary"
          );
          setData([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchInventory();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return {
    data,
    loading,
    error
  };
};